/**
 * Screener page pure helpers (Screener Phase 3, 2026-10-28): moved verbatim out of src/pages/ScreenView.tsx.
 * Display tones, payload readers, the local contract-quality fallback and the allocation-invariant wording.
 * Nothing here ranks, sizes or stages anything. The pure, unit-tested truth helpers stay in screenerTruth.ts.
 */
import { numberFmt, roundTripSpreadPct, titleizeBucket, titleizeCode, vegaPer1k } from "@lib/screenerTruth";
import type {
  AllocationValidation,
  AllocationValidationViolation,
  OICaps,
  Purchase,
  ScreenerRiskContext,
  SortKey,
  TilesMeta,
  Tone,
  UniverseGrade,
} from "@lib/screenerTypes";

export const structuralAnchor = "24_30m";

// REMOVED 2026-08-13: the Vega/NAV "suggested cap" bps table (capForZ).
//
// Byte-identical to the constant REMOVED from SignalsDashboard on 2026-07-31 as audit
// Tier-0 #12; that removal record still stands at SignalsDashboard.tsx:975 and states
// the table is "NON-MONOTONIC AND INVERTED at the calm end" and that "IT CONTRADICTS
// POLICY" — Sweet-Spot, Lean-In and Very-Calm all target 40% funded, so a band that
// descends across them tells you to cut exposure exactly where policy wants it full. It
// survived here because this file kept its own copy. Applying the existing CIO decision to
// the second copy; no new decision is being made.
//
// It was display-only: the screener request body hard-codes suggested_vega_cap_bps_low,
// suggested_vega_cap_bps_high and portfolio_nav to null, so this value never reached the
// backend, the ranking or the allocator. If a vega budget is worth having it should be
// AUTHORED beside the regime bands and surfaced from the backend, not inherited from a UI
// constant.

export function downloadExcelWorkbook(filename: string, html: string) {
  const blob = new Blob(["﻿", html], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Build the oi_caps request override from the four tier inputs.
 *
 * CIO decision 2026-09-10 (screener liquidity-gate audit, Step 1). Returns null when
 * every tier is blank, so the request omits oi_caps entirely and
 * HelionBackend/entry_signals.json decides. Returns a PARTIAL dict when only some
 * tiers are filled -- the backend merges a partial over config, so an untouched tier
 * keeps its configured value instead of being overwritten by a UI default.
 *
 * This replaces an always-fully-populated object built from hard-coded UI defaults,
 * which is how the live screen ran oi_caps.under_100 at 0.05 for months while the
 * config file said 0.0 and nothing surfaced the disagreement.
 */
export function buildOiCapsOverride(raw: Record<string, string>): OICaps | null {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (value == null || String(value).trim() === "") continue;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) continue;
    out[key] = parsed;
  }
  return Object.keys(out).length ? (out as OICaps) : null;
}

export function firstNumber(...values: any[]): number | null {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

export function firstString(...values: any[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

export function regimeBandFromZ(z?: number | null): string | null {
  if (z == null || Number.isNaN(z)) return null;
  if (z >= 2.5) return "Panic";
  if (z >= 1.0) return "High-Vol";
  if (z >= -1.25) return "Sweet-Spot";
  if (z >= -1.5) return "Lean-In";
  return "Very-Calm";
}

export function toneFromValueLabel(label?: string | null): Tone {
  switch ((label || "").toLowerCase()) {
    case "very_cheap":
    case "cheap":
      return "success";
    case "rich":
      return "warning";
    case "very_rich":
      return "danger";
    default:
      return "neutral";
  }
}

export function toneFromShapeLabel(label?: string | null): Tone {
  switch ((label || "").toLowerCase()) {
    case "normal":
      return "success";
    case "flat":
      return "info";
    case "elevated":
      return "warning";
    case "dislocated":
      return "danger";
    default:
      return "neutral";
  }
}

export function toneFromTenorBucket(bucket?: string | null): Tone {
  switch ((bucket || "").toLowerCase()) {
    case "24_30m":
      return "success";
    case "30m_plus":
      return "info";
    case "18_24m":
      return "warning";
    default:
      return "neutral";
  }
}


export function topCountLabel(obj?: Record<string, number>): string {
  if (!obj || Object.keys(obj).length === 0) return "—";
  const [k, v] = Object.entries(obj).sort((a, b) => b[1] - a[1])[0];
  return `${titleizeBucket(k)} (${v})`;
}



export function toneFromDeploymentMode(mode?: string | null): Tone {
  switch ((mode || "").toLowerCase()) {
    case "aggressive":
      return "success";
    case "selective":
      return "warning";
    case "defensive":
      return "danger";
    default:
      return "info";
  }
}

// titleizeReason removed 2026-10-28 (Phase 2) with its two consumers, the Rotation Reason tile and Why This Bucket Won.



export function toneFromEntryGrade(grade?: string | null): Tone {
  switch ((grade || "").toUpperCase()) {
    case "A":
      return "success";
    case "B":
      return "info";
    case "C":
      return "neutral";
    case "D":
      return "warning";
    case "F":
      return "danger";
    default:
      return "neutral";
  }
}

// ---------------------------------------------------------------------------
// EXECUTION-COST TRANSPARENCY (added 2026-08-11). Display only — these feed no
// score, filter, cap, grade or allocation decision. They exist because the entry
// grade is computed BEFORE any friction is charged, so the page could stamp
// "A - highest conviction" on a contract whose round-trip spread was ~14% of
// premium and say nothing about it.
//
// Both are derived from fields already in the payload (bid/ask/mid/vega), so
// nothing on the backend changes.
// ---------------------------------------------------------------------------


/** DISPLAY thresholds only — not decision rules, not sourced from any study,
 *  and safe to retune. They exist so a wide spread is visible at a glance
 *  rather than buried in a number. */
export function toneFromSpread(pct: number | null): Tone {
  if (pct == null) return "neutral";
  if (pct <= 0.02) return "success";
  if (pct <= 0.05) return "info";
  if (pct <= 0.10) return "warning";
  return "danger";
}

export function universeGradeFromScore(score: number): string {
  if (score >= 0.8) return "A";
  if (score >= 0.68) return "B";
  if (score >= 0.55) return "C";
  if (score >= 0.42) return "D";
  return "F";
}

export function universeGradeLabel(grade?: string | null): string {
  switch ((grade || "F").toUpperCase()) {
    case "A":
      return "Excellent universe";
    case "B":
      return "Strong universe";
    case "C":
      return "Usable universe";
    case "D":
      return "Thin / selective universe";
    default:
      return "Poor universe";
  }
}

export function localUniverseGrade(rows: Purchase[]): UniverseGrade {
  const total = rows.length;
  if (!total) {
    return {
      grade: "F",
      score: 0,
      label: "No qualifying universe",
      summary: "No contracts passed the screener filters.",
      components: {
        candidateCount: 0,
        selectedCount: 0,
        skippedCount: 0,
        avgEntryScore: null,
        topEntryScore: null,
        avgSurfaceScore: null,
        avgMlScore: null,
        abShare: 0,
        cOrBetterShare: 0,
        selectedDepthScore: 0,
        entryGradeCounts: {},
      },
    };
  }

  const avg = (values: Array<number | null | undefined>) => {
    const clean = values.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
    return clean.length ? clean.reduce((a, b) => a + b, 0) / clean.length : null;
  };

  const entryScores = rows
    .map((r) => r.entryScore)
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  const surfaceScores = rows
    .map((r) => r.surfaceScore)
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  const mlScores = rows
    .map((r) => r.mlScore)
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));

  const gradeCounts = rows.reduce<Record<string, number>>((acc, row) => {
    const grade = (row.entryGrade || "F").toUpperCase();
    acc[grade] = (acc[grade] || 0) + 1;
    return acc;
  }, {});

  const selectedCount = rows.filter(isSelectedCandidate).length;
  const avgEntryScore = avg(entryScores);
  const topEntryScore = avg([...entryScores].sort((a, b) => b - a).slice(0, Math.max(1, Math.min(5, entryScores.length))));
  const avgSurfaceScore = avg(surfaceScores);
  const avgMlScore = avg(mlScores);
  const abShare = ((gradeCounts.A || 0) + (gradeCounts.B || 0)) / total;
  const cOrBetterShare = ((gradeCounts.A || 0) + (gradeCounts.B || 0) + (gradeCounts.C || 0)) / total;
  const selectedDepthScore = Math.min(1, selectedCount / Math.max(1, Math.min(3, total)));
  const score = Math.max(
    0,
    Math.min(
      1,
      0.35 * (avgEntryScore ?? 0) +
        0.3 * (topEntryScore ?? 0) +
        0.25 * (avgSurfaceScore ?? 0) +
        0.1 * cOrBetterShare
    )
  );
  const grade = universeGradeFromScore(score);

  return {
    grade,
    score,
    label: universeGradeLabel(grade),
    summary:
      grade === "A"
        ? "Broad high-quality opportunity set; top candidates and overall breadth are aligned."
        : grade === "B"
        ? "Good opportunity set with enough quality to support deployment, subject to normal CIO review."
        : grade === "C"
        ? "Mixed but usable universe; selected lines may be fine, but breadth is only moderate."
        : grade === "D"
        ? "Low-quality or narrow universe; stage only with strong reason and inspect skipped lines carefully."
        : "Weak opportunity set; the filtered universe is not showing enough score quality or breadth.",
    components: {
      candidateCount: total,
      selectedCount,
      skippedCount: Math.max(0, total - selectedCount),
      avgEntryScore,
      topEntryScore,
      avgSurfaceScore,
      avgMlScore,
      abShare,
      cOrBetterShare,
      selectedDepthScore,
      entryGradeCounts: gradeCounts,
    },
  };
}

export function isSelectedCandidate(p: Purchase): boolean {
  const status = String(p.allocationStatus || "").toUpperCase();
  if (status !== "BUY") return false;
  return Number(p.allocatedQty || 0) > 0;
}

export function isRankedCandidate(p: Purchase): boolean {
  const status = String(
    p.screenDecision || p.decisionStatus || p.decision || p.allocationStatus || p.screenStatus || ""
  ).toUpperCase();
  return status === "RANKED" || (!isSelectedCandidate(p) && !p.skipReason);
}

export function titleizeSkipReason(reason?: string | null): string {
  if (!reason) return "Selected";
  return reason.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function invariantValueFmt(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (Math.abs(value) >= 1000) return numberFmt(value, 0);
    if (Math.abs(value) >= 1) return numberFmt(value, 2);
    return numberFmt(value, 4);
  }
  if (typeof value === "string" && value.trim()) return value.trim();
  return "—";
}

export function allocationViolationTitle(code?: string | null): string {
  if (!code) return "Allocation invariant";
  switch (code) {
    case "max_tranches_total":
      return "Total tranche cap";
    case "capital_to_deploy":
      return "Deployment budget";
    case "run_vega_budget":
      return "Global vega budget";
    case "per_exp_notional_cap":
      return "Per-expiration notional cap";
    case "per_exp_vega_cap":
      return "Per-expiration vega cap";
    case "max_tranches_per_expiration":
      return "Per-expiration tranche cap";
    case "min_contracts_for_home_run_trim":
      return "Two-contract runner minimum";
    case "oi_cap":
      return "Open-interest cap";
    case "line_share_cap":
      return "Line-share cap";
    default:
      return titleizeSkipReason(code);
  }
}

export function allocationViolationDetail(v: AllocationValidationViolation): string {
  const scope = [
    v.expiry ? `expiry ${v.expiry}` : null,
    v.conid ? `conid ${String(v.conid)}` : null,
  ].filter(Boolean).join(" · ");
  const comparison = `actual ${invariantValueFmt(v.actual)} vs limit ${invariantValueFmt(v.limit)}`;
  return scope ? `${scope} · ${comparison}` : comparison;
}

export function toneFromAllocationValidation(validation?: AllocationValidation | null): Tone {
  if (!validation) return "neutral";
  if (String(validation.status || "").toLowerCase() === "pass") return "success";
  if (String(validation.status || "").toLowerCase() === "fail") return "danger";
  return "neutral";
}

// deploymentSignalTone removed 2026-08-11 — its only caller was
// deploymentInterpretation, which went with the Deployment Interpretation card.

export function toneFromTrustStatus(status?: string | null): Tone {
  const s = String(status || "").toLowerCase();
  if (s === "pass" || s === "ok") return "success";
  if (s === "review" || s === "info") return "info";
  if (s === "warn" || s === "warning") return "warning";
  if (s === "fail" || s === "halt" || s === "error") return "danger";
  return "neutral";
}



export function topCountEntries(record?: Record<string, number> | null, limit = 4): Array<[string, number]> {
  if (!record) return [];
  return Object.entries(record)
    .map(([k, v]) => [k, Number(v || 0)] as [string, number])
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);
}

export function topCountText(record?: Record<string, number> | null, limit = 4): string {
  const entries = topCountEntries(record, limit);
  return entries.length ? entries.map(([k, v]) => `${titleizeCode(k)} (${v})`).join(" · ") : "—";
}

export function contributionTone(value?: number | null): Tone {
  if (value == null || Number.isNaN(value)) return "neutral";
  if (value >= 0.22) return "success";
  if (value >= 0.08) return "info";
  if (value >= 0) return "neutral";
  if (value <= -0.18) return "danger";
  return "warning";
}

/** The ranked table's sort value for one row (moved out of the ScreenView component, Screener Phase 3). */
export function getCellValue(p: Purchase, key: SortKey): string | number | null {
  switch (key) {
    case "priorityRank":
      // Missing (older backend) sorts as unknown; the comparator then keeps the payload's
      // own order, which is Entry Score -- and the Order toggle says so.
      return typeof p.priorityRank === "number" ? p.priorityRank : null;
    case "allocationStatus":
      return isRankedCandidate(p) ? "RANKED" : isSelectedCandidate(p) ? "BUY" : "SKIPPED";
    case "skipReason":
      return isRankedCandidate(p) ? "Market-ranked" : p.skipReason || p.selectionReason || "Selected allocation";
    case "entryGrade":
      return p.entryGrade || "";
    // Sort with unknown cost LAST in both directions rather than as zero — an
    // incomplete quote must never sort as if it were the tightest market.
    case "roundTripSpreadPct":
      return roundTripSpreadPct(p) ?? Infinity;
    case "vegaPer1k":
      return vegaPer1k(p) ?? -Infinity;
    case "expiry":
      return p.expiry;
    case "right":
      return p.right;
    case "strike":
      return p.strike;
    case "dte":
      return p.dte;
    case "conid":
      return typeof p.conid === "string" ? p.conid : Number(p.conid);
    case "bid":
      return p.bid ?? -Infinity;
    case "ask":
      return p.ask ?? -Infinity;
    case "mid":
      return p.mid ?? p.midPrice ?? -Infinity;
    case "unitCost":
      return p.unitCost ?? ((p.mid ?? p.midPrice ?? 0) * (p.multiplier || 100));
    case "vega":
      return p.vega ?? -Infinity;
    case "openInterest":
      return p.openInterest ?? -Infinity;
    case "allocatedQty":
      return p.allocatedQty;
    case "estNotional":
      return p.estNotional;
    case "estVegaUSDperPt":
      return p.estVegaUSDperPt;
    case "entryScore":
      return p.entryScore ?? -Infinity;
    case "surfaceScore":
      return p.surfaceScore ?? -Infinity;
    case "surfaceScoreNorm":
      return p.surfaceScoreNorm ?? -Infinity;
    case "surfaceRegime":
      return p.surfaceRegime ?? "";
    case "surfaceValueLabel":
      return p.surfaceValueLabel ?? "";
    case "surfaceShapeLabel":
      return p.surfaceShapeLabel ?? "";
    case "tenorBucket":
      return p.tenorBucket ?? "";
    case "tenorOpportunityScore":
      return p.tenorOpportunityScore ?? -Infinity;
    case "iv":
      return p.iv ?? -Infinity;
    case "moneyness":
      return p.moneyness ?? -Infinity;
  }
}

/**
 * The page's risk context from risk/kpis (+ surface_overview), moved verbatim out of ScreenView's load effect
 * (Screener Phase 3, 2026-10-28). Pure: the effect still owns the fetches, the error states and the setters.
 */
export function buildRiskContext(kpiPayload: any, surfacePayload: any, tilesMeta: TilesMeta): ScreenerRiskContext {
  const summary = kpiPayload?.summary || {};
  const surfaceSummary = surfacePayload?.surface_summary || {};
  const vixZ = firstNumber(summary?.vix_zscore, kpiPayload?.vix_zscore, tilesMeta?.vixZScore);
  const portfolioNav = firstNumber(
    summary?.portfolio_nav,
    summary?.portfolioNav,
    summary?.total_nav,
    summary?.totalNav,
    summary?.nav,
    summary?.NAV,
    summary?.net_liquidation,
    summary?.netLiquidation,
    summary?.total_net_liquidation,
    summary?.totalNetLiquidation,
    kpiPayload?.portfolio_nav,
    kpiPayload?.total_nav,
    kpiPayload?.nav,
    kpiPayload?.totalNav
  );

  return {
    baseRegimeBand: firstString(
      summary?.base_regime_band,
      summary?.baseRegimeBand,
      summary?.current_band,
      summary?.currentBand,
      regimeBandFromZ(vixZ)
    ),
    effectiveRegime: firstString(
      summary?.effective_regime,
      summary?.effectiveRegime,
      surfaceSummary?.deployment_stance
    ),
    defenseOverlayState: firstString(
      summary?.defense_overlay_state,
      summary?.defenseOverlayState,
      summary?.defense_overlay_label,
      summary?.defenseOverlayLabel
    ),
    defenseOverlayActive: Boolean(
      summary?.defense_overlay_active ??
      summary?.defenseOverlayActive ??
      summary?.defense_confirmed ??
      summary?.defenseConfirmed ??
      false
    ),
    targetLeapsPct: firstNumber(
      summary?.effective_target_leaps_pct,
      summary?.effectiveTargetLeapsPct,
      summary?.target_leaps_pct,
      summary?.targetLeapsPct,
      summary?.base_target_leaps_pct,
      summary?.baseTargetLeapsPct,
      summary?.max_leaps_pct,
      summary?.maxLeapsPct
    ),
    // MARKET-value weight. Named honestly now: leaps_market_pct is what this has
    // always been, despite the legacy key being called leaps_funded_pct. Market
    // weight above the 40% cap is EXPECTED (57.8% of days on the 2013-2026
    // baseline) and must NOT be compared against the funded cap.
    currentLeapsAllocationPct: firstNumber(
      summary?.current_leaps_allocation_pct,
      summary?.currentLeapsAllocationPct,
      summary?.leaps_market_pct,
      summary?.leaps_funded_pct, // DEPRECATED alias: also market value
      summary?.leapsFundedPct
    ),
    // FUNDED / cost-basis weight — the only quantity the 40% cap governs. This
    // chain previously found nothing (none of these keys were ever emitted), so
    // the cap comparison silently fell back to market value.
    basisLeapsAllocationPct: firstNumber(
      summary?.leaps_funded_cost_pct,
      summary?.current_leaps_basis_allocation_pct,
      summary?.currentLeapsBasisAllocationPct,
      summary?.leaps_basis_pct,
      summary?.leapsBasisPct,
      summary?.cost_basis_leaps_pct,
      summary?.costBasisLeapsPct,
      summary?.funded_basis_pct,
      summary?.fundedBasisPct
    ),
    basisTargetLeapsPct: firstNumber(
      summary?.effective_target_leaps_basis_pct,
      summary?.effectiveTargetLeapsBasisPct,
      summary?.target_leaps_basis_pct,
      summary?.targetLeapsBasisPct,
      summary?.effective_target_leaps_pct,
      summary?.effectiveTargetLeapsPct,
      summary?.target_leaps_pct,
      summary?.targetLeapsPct
    ),
    // CORRECTED 2026-08-18 (FI target-vs-held audit, item 5). The chain above
    // resolves to summary.effective_target_leaps_pct = target_pct_effective --
    // the effective REGIME TARGET, lowered by early-defense and by the grind
    // floor (default 0.10). That is NOT the cap. The cap is published separately
    // as summary.funded_cap_pct (risk_kpis/__init__.py:1393 <- caps.funded_max,
    // the same 40% the Signals page shows) and is the only number the word "cap"
    // may render. No fallback to the target: if the cap is absent the tile shows
    // "-", because a defense-lowered target printed under "cap" reads as a breach
    // that funded_cap_breached says is not happening.
    fundedCapPct: firstNumber(
      summary?.funded_cap_pct,
      summary?.fundedCapPct,
      summary?.max_leaps_pct,
      summary?.maxLeapsPct
    ),
    portfolioNav,
    bestDeploymentBucket: firstString(
      summary?.best_deployment_bucket,
      summary?.bestDeploymentBucket,
      surfaceSummary?.best_deployment_bucket,
      surfaceSummary?.bestDeploymentBucket,
      surfaceSummary?.sweet_spot_bucket,
      surfaceSummary?.sweetSpotBucket
    ),
    surfaceAlignmentPct: firstNumber(
      summary?.surface_alignment_pct,
      summary?.surfaceAlignmentPct,
      summary?.surface_alignment_score,
      summary?.surfaceAlignmentScore,
      surfaceSummary?.surface_alignment_pct,
      surfaceSummary?.surfaceAlignmentPct,
      surfaceSummary?.surface_alignment_score,
      surfaceSummary?.surfaceAlignmentScore
    ),
    engineEffectiveRegime: firstString(summary?.effective_regime, summary?.effectiveRegime),
    regimeContextSource: firstString(summary?.regime_context_source, summary?.regimeContextSource),
    navAsOf: firstString(summary?.latest_nav_as_of, summary?.latestNavAsOf),
    vixAsOf: firstString(summary?.latest_vix_as_of, summary?.latestVixAsOf),
  };
}
